import React, { useState, useEffect } from 'react';
import {
    Box, Button, TextField, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Typography, IconButton, Dialog,
    DialogTitle, DialogContent, DialogActions, Grid, Stack, Autocomplete, 
    CircularProgress, TablePagination, InputAdornment
} from '@mui/material';
import { Add, Edit, Delete, Print, Search } from '@mui/icons-material';
import { collection, addDoc, getDocs, getDoc, query, orderBy, deleteDoc, doc, updateDoc, where, Timestamp } from "firebase/firestore";
import { db, auth } from "../../firebase";
import Swal from 'sweetalert2';

const PRIMARY = "#0d9488";

function Hosteloutpass() {
    const [outPasses, setOutPasses] = useState([]);
    const [students, setStudents] = useState([]); 
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [schoolId, setSchoolId] = useState(null);
    const [printData, setPrintData] = useState(null);

    // Pagination State
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [formData, setFormData] = useState({
        studentId: '',
        studentName: '',
        gradeClass: '',
        parentsName: '',
        parentsContact: '',
        parentsAdhar: '',
        day: '',
        date: new Date().toISOString().split('T')[0],
        outTime: '',
        roomNo: '',
        emergencyNo: '',
        reason: '',
        fromDate: new Date().toISOString().split('T')[0],
        toDate: '',
        totalDays: '',
        expectedReturnTime: '',
        returnDate: ''
    });

    useEffect(() => {
        const init = async () => {
            if (!auth.currentUser) return;
            try {
                const userSnap = await getDoc(doc(db, "users", auth.currentUser.uid));
                if (userSnap.exists()) {
                    const sId = userSnap.data().schoolId;
                    setSchoolId(sId);
                    fetchBoardingStudents(sId);
                    fetchOutPasses(sId);
                }
            } catch (err) {
                console.error("Initialization error:", err);
            }
        };
        init();
    }, []);

    const fetchBoardingStudents = async (sId) => {
        try {
            const [admSnap, studentSnap, courseSnap, roomSnap] = await Promise.all([
                getDocs(query(collection(db, "admission"), where("schoolId", "==", sId), where("studenttype", "==", "Boarding"), where('status', '==', 'Active'))),
                getDocs(query(collection(db, "students"), where("schoolId", "==", sId))),
                getDocs(query(collection(db, "courses"), where("schoolId", "==", sId))),
                getDocs(query(collection(db, "roomallocations"), where("schoolId", "==", sId))) 
            ]);

            const studentMap = {};
            studentSnap.docs.forEach(d => { studentMap[d.id] = d.data(); });

            const courseMap = {};
            courseSnap.docs.forEach(d => { courseMap[d.id] = d.data(); });

            const roomMap = {};
            roomSnap.docs.forEach(d => {
                const data = d.data();
                roomMap[data.studentId] = data.roomId;
            });

            const list = admSnap.docs.map((adm) => {
                const admData = adm.data();
                const sData = studentMap[admData.studentId];
                return {
                    id: admData.studentId,
                    name: `${sData?.firstname || ''} ${sData?.fathername || ''} ${sData?.surname || ''}`.trim().toUpperCase(),
                    className: courseMap[admData.class]?.name || "N/A",
                    parentName: sData?.fathername || '',
                    parentContact: sData?.fathermobile || '',
                    adhar: sData?.adharno || '',
                    roomNo: roomMap[admData.studentId] || 'Not Assigned', 
                    label: `${sData?.firstname || ''} ${sData?.fathername || ''} ${sData?.surname || ''} - ${courseMap[admData.class]?.name || ''}`.trim().toUpperCase()
                };
            });
            setStudents(list);
        } catch (err) { 
            console.error("Error fetching students/rooms:", err); 
        }
    };

    const fetchOutPasses = async (sId) => {
        try {
            // LIFO Sorted by timestamp descending
            const q = query(collection(db, "hostel_out_passes"), where("schoolId", "==", sId), orderBy("timestamp", "desc"));
            const snap = await getDocs(q);
            setOutPasses(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (err) {
            console.error("Error fetching out passes:", err);
        }
    };

    const handleStudentSelect = (val) => {
        if (val) {
            setFormData(prev => ({
                ...prev,
                studentId: val.id,
                studentName: val.name,
                gradeClass: val.className,
                parentsName: val.parentName,
                parentsContact: val.parentContact,
                parentsAdhar: val.adhar,
                roomNo: val.roomNo 
            }));
        } else {
            resetForm();
        }
    };

    const resetForm = () => {
        setFormData({
            studentId: '',
            studentName: '',
            gradeClass: '',
            parentsName: '',
            parentsContact: '',
            parentsAdhar: '',
            day: '',
            date: new Date().toISOString().split('T')[0],
            outTime: '',
            roomNo: '',
            emergencyNo: '',
            reason: '',
            fromDate: new Date().toISOString().split('T')[0],
            toDate: '',
            totalDays: '',
            expectedReturnTime: '',
            returnDate: ''
        });
        setEditId(null);
    };

    const handleSave = async () => {
        if (!formData.studentId) return Swal.fire("Error", "Please select a student", "error");
        setLoading(true);
        try {
            const data = { ...formData, schoolId, timestamp: Timestamp.now() };
            if (editId) {
                await updateDoc(doc(db, "hostel_out_passes", editId), data);
                Swal.fire("Success", "Out Pass Updated", "success");
            } else {
                const srNo = outPasses.length > 0 ? (Math.max(...outPasses.map(o => o.srNo || 0)) + 1) : 1;
                await addDoc(collection(db, "hostel_out_passes"), { ...data, srNo });
                Swal.fire("Success", "Out Pass Saved", "success");
            }
            setOpen(false);
            resetForm();
            fetchOutPasses(schoolId);
        } catch (e) { 
            Swal.fire("Error", e.message, "error"); 
        }
        setLoading(false);
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "This process cannot be undone!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: PRIMARY,
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await deleteDoc(doc(db, "hostel_out_passes", id));
                fetchOutPasses(schoolId);
                Swal.fire('Deleted!', 'Out pass has been deleted.', 'success');
            } catch (error) {
                Swal.fire('Error', 'Failed to delete data.', 'error');
            }
        }
    };

    const triggerPrint = (item) => {
        setPrintData(item);
        setTimeout(() => { 
            window.print(); 
        }, 500);
    };

    // Client-side real-time filtering logic
    const filteredOutPasses = outPasses.filter(item => {
        const term = search.toLowerCase();
        return (
            item.studentName?.toLowerCase().includes(term) ||
            item.gradeClass?.toLowerCase().includes(term) ||
            item.reason?.toLowerCase().includes(term) ||
            item.srNo?.toString().includes(term)
        );
    });

    // Pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <Box p={3} sx={{ bgcolor: "#f8fafc", minHeight: "100vh" }}>
            <style>
                {`
                @media print {
                    body * { visibility: hidden; }
                    #print-area, #print-area * { visibility: visible; }
                    #print-area { position: absolute; left: 0; top: 0; width: 100%; display: block !important; }
                }
                .pass-card { border: 2px solid #000; padding: 25px; font-family: serif; background: #fff; }
                .line { border-bottom: 1px dotted #000; display: inline-block; padding: 0 5px; font-weight: bold; }
                `}
            </style>

            {/* Header Controls */}
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={3}>
                <Typography variant="h5" fontWeight="bold">Hostel Student Out-Pass Panel</Typography>
                {/* <Button 
                    variant="contained" 
                    sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: '#0f766e' } }} 
                    startIcon={<Add />} 
                    onClick={() => { resetForm(); setOpen(true); }}
                >
                    New Out Pass
                </Button> */}
            </Stack>

            {/* Search Box Card Layout */}
            <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid #e2e8f0', borderRadius: 2 }}>
                <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    placeholder="Search by Student Name, Class, Reason or Serial No..."
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search sx={{ color: 'text.secondary' }} />
                            </InputAdornment>
                        ),
                    }}
                />
            </Paper>

            {/* Main Data Table */}
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
                <Table size="small">
                    <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                        <TableRow>
                            <TableCell style={{ fontWeight: 'bold' }}>Sr.</TableCell>
                            <TableCell style={{ fontWeight: 'bold' }}>Date</TableCell>
                            <TableCell style={{ fontWeight: 'bold' }}>Student Name</TableCell>
                            <TableCell style={{ fontWeight: 'bold' }}>Class / Room</TableCell>
                            <TableCell style={{ fontWeight: 'bold' }}>Reason</TableCell>
                            <TableCell style={{ fontWeight: 'bold' }}>Duration</TableCell>
                            <TableCell align="center" style={{ fontWeight: 'bold' }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredOutPasses.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                    No records matching the selection criteria.
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredOutPasses
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((item) => (
                                    <TableRow key={item.id} hover>
                                        <TableCell>{item.srNo}</TableCell>
                                        <TableCell>{item.date}</TableCell>
                                        <TableCell><b>{item.studentName}</b></TableCell>
                                        <TableCell>{item.gradeClass} (RM: {item.roomNo || 'N/A'})</TableCell>
                                        <TableCell>{item.reason}</TableCell>
                                        <TableCell>{item.fromDate} to {item.toDate || item.date}</TableCell>
                                        <TableCell align="center">
                                            <Stack direction="row" spacing={0.5} justifyContent="center">
                                                <IconButton color="primary" onClick={() => triggerPrint(item)}>
                                                    <Print fontSize="small" />
                                                </IconButton>
                                                {/* <IconButton color="info" onClick={() => { setEditId(item.id); setFormData(item); setOpen(true); }}>
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                                <IconButton color="error" onClick={() => handleDelete(item.id)}>
                                                    <Delete fontSize="small" />
                                                </IconButton> */}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredOutPasses.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </TableContainer>

            {/* Input Entry Modal Form */}
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle fontWeight="bold">{editId ? "Modify Existing Out Pass" : "Issue New Hostel Out Pass"}</DialogTitle>
                <DialogContent dividers>
                    <Grid className="row" spacing={2} sx={{ mt: 0.5 }}>
                        <Grid className="col-sm-4 mb-3" item xs={12} sm={6}>
                            <Autocomplete
                                options={students}
                                getOptionLabel={(o) => o.label || ''}
                                value={students.find(s => s.id === formData.studentId) || null}
                                onChange={(e, v) => handleStudentSelect(v)}
                                renderInput={(params) => <TextField {...params} label="Select Boarding Student" size="small" required />}
                            />
                        </Grid>
                        <Grid className="col-sm-4 mb-3" item xs={6} sm={3}>
                            <TextField fullWidth label="Date" type="date" size="small" InputLabelProps={{ shrink: true }} value={formData.date} onChange={e => setFormData({ ...formData, date: e.target.value })} />
                        </Grid>
                        <Grid className="col-sm-4 mb-3" item xs={6} sm={3}>
                            <TextField fullWidth type='time' label="Out Time" size="small" InputLabelProps={{ shrink: true }} value={formData.outTime} onChange={e => setFormData({ ...formData, outTime: e.target.value })} />
                        </Grid>

                        <Grid className="col-sm-4 mb-3" item xs={12} sm={8}>
                            <TextField disabled fullWidth label="Student Name" size="small" value={formData.studentName} />
                        </Grid>
                        <Grid className="col-sm-4 mb-3" item xs={6} sm={2}>
                            <TextField disabled fullWidth label="Class" size="small" value={formData.gradeClass} />
                        </Grid>
                        <Grid className="col-sm-4 mb-3" item xs={6} sm={2}>
                            <TextField fullWidth label="Room No" size="small" value={formData.roomNo} onChange={e => setFormData({ ...formData, roomNo: e.target.value })} />
                        </Grid>

                        <Grid className="col-sm-4 mb-3" item xs={12} sm={6}>
                            <TextField fullWidth label="Parents Name" size="small" value={formData.parentsName} onChange={e => setFormData({ ...formData, parentsName: e.target.value })} />
                        </Grid>
                        <Grid className="col-sm-4 mb-3" item xs={12} sm={6}>
                            <TextField fullWidth label="Parents Contact" size="small" value={formData.parentsContact} onChange={e => setFormData({ ...formData, parentsContact: e.target.value })} />
                        </Grid>

                        <Grid className="col-sm-4 mb-3" item xs={12}>
                            <TextField fullWidth label="Reason for Leave" size="small" value={formData.reason} onChange={e => setFormData({ ...formData, reason: e.target.value })} />
                        </Grid>

                        <Grid className="col-sm-4 mb-3" item xs={6} sm={4}>
                            <TextField fullWidth label="From Date" type="date" size="small" InputLabelProps={{ shrink: true }} value={formData.fromDate} onChange={e => setFormData({ ...formData, fromDate: e.target.value })} />
                        </Grid>
                        <Grid className="col-sm-4 mb-3" item xs={6} sm={4}>
                            <TextField fullWidth label="To Date" type="date" size="small" InputLabelProps={{ shrink: true }} value={formData.toDate} onChange={e => setFormData({ ...formData, toDate: e.target.value })} />
                        </Grid>
                        <Grid className="col-sm-4 mb-3" item xs={12} sm={4}>
                            <TextField fullWidth label="Total Days" size="small" value={formData.totalDays} onChange={e => setFormData({ ...formData, totalDays: e.target.value })} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setOpen(false)} color="inherit">Cancel</Button>
                    <Button variant="contained" sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: '#0f766e' } }} onClick={handleSave} disabled={loading}>
                        {loading ? <CircularProgress size={24} /> : "Save Pass"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* A5 Landscape Print Wrapper Template */}
            <div id="print-area" style={{ display: 'none' }}>
                {printData && (
                    <div className="pass-card" style={{
                        width: '190mm',
                        minHeight: '138mm', 
                        padding: '20px',
                        backgroundColor: 'white',
                        margin: '0 auto',
                        boxSizing: 'border-box'
                    }}>
                        <Box textAlign="center" borderBottom="2px solid #000" pb={1} mb={3}>
                            <Typography variant="h5" fontWeight="900" style={{ letterSpacing: '1px' }}>MOHAN MALI INTERNATIONAL SCHOOL</Typography>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 0.5 }}>HOSTEL STUDENT OUT PASS</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" mb={3}>
                            <Typography>Pass No: <span className="line" style={{ minWidth: '60px' }}>{printData.srNo}</span></Typography>
                            <Typography>Date: <span className="line">{printData.date}</span></Typography>
                        </Box>
                        <Box sx={{ lineHeight: 3 }}>
                            <Typography>This is to permit Master/Miss: <span className="line" style={{ width: '65%' }}>{printData.studentName}</span></Typography>
                            <Typography>Class: <span className="line" style={{ width: '25%' }}>{printData.gradeClass}</span> Room No: <span className="line" style={{ width: '25%' }}>{printData.roomNo}</span></Typography>
                            <Typography>Reason for Leave: <span className="line" style={{ width: '77%' }}>{printData.reason}</span></Typography>
                            <Typography>Leave Duration: From <span className="line">{printData.fromDate}</span> To <span className="line">{printData.toDate || printData.date}</span> Total Days: <span className="line" style={{ minWidth: '40px' }}>{printData.totalDays || 'N/A'}</span></Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" mt={8} px={2}>
                            <Typography fontWeight="bold" style={{ borderTop: '1px solid #000', paddingTop: '5px', width: '100px', textAlign: 'center' }}>Parent Sign</Typography>
                            <Typography fontWeight="bold" style={{ borderTop: '1px solid #000', paddingTop: '5px', width: '100px', textAlign: 'center' }}>Warden Sign</Typography>
                            <Typography fontWeight="bold" style={{ borderTop: '1px solid #000', paddingTop: '5px', width: '100px', textAlign: 'center' }}>Principal Sign</Typography>
                        </Box>
                    </div>
                )}
            </div>
        </Box>
    );
}

export default Hosteloutpass;